import { useEffect, useState, useContext } from "react";
import { makeStyles } from "@mui/styles";
import {
  IconButton,
  Box,
  Tooltip,
  Typography,
  Modal,
  Card,
  Button,
  CardContent,
  Divider,
  CardHeader,
  CardActions,
  Popover,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import GuidedTourTooltip, { GuidedTourTooltipProps } from "./GuidedTourTooltip";
import GuidedTourReminder from "./GuidedTourReminder";
import { useHistory } from "react-router-dom";
import { UniversalModalCtx, UserCtx, CourseStructureCtx } from "../../interfaces";

import { IconButtonContained } from "./utils/IconButtonContained";
import { UniversalModalContext } from "../../context/UniversalModalContext";
import { UserContext } from "../../context/UserContext";
import { CourseStructureContext } from "../../context/CourseStructureContext";
import { HelpAndResourcesOptions } from "../../enums";
import { generateGtmEvent, getSupportLink } from "../../Helpers";
import useStorageState from "../../hooks/useStorageState";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "fixed",
    zIndex: 100,
  },
  visuallyHidden: {
    position: "absolute",
    width: "1px", //Shrinks the element to a 1x1 pixel size, making it effectively invisible.
    height: "1px",
    margin: "-1px",
    padding: 0,
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)", //Clips the element to an invisible rectangle, effectively hiding it.
    whiteSpace: "nowrap",
    border: 0,
  },
  paperRoot: {
    background: "none",
    overflow: "visible",
  },
  menuRoot: {
    transform: "translateY(-24px) translateX(44px)",
    backgroundColor: "#FFC627",
    boxShadow: "0 8px 12px 0 rgba(0, 0, 0, 0.16)",
    padding: "16px",
    borderRadius: "16px",
    overflow: "visible",
    textAlign: "right",
  },
  button: {
    position: "relative",
    boxSizing: "border-box",
    height: 44,
    transitionProperty: "width, border-radius",
    transitionDuration: "100ms",
    transitionTimingFunction: "ease-out",
    backgroundColor: "#FFC627 !important",
    color: `${theme.palette.text.primary} !important`,
    "&:hover": {
      backgroundColor: "#B38300 !important",
    },
    "&:focus": {
      backgroundColor: "#FFCF4A !important",
    },
    "&:focus-visible": {
      backgroundColor: "#FFCF4A !important",
      border: "2px solid #191919",
    },
  },
  guidedTourModal: {
    position: "absolute",
    width: "500px",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  modalTitle: {
    fontSize: "1.25em",
  },
  modalCloseButton: {
    color: theme.palette.text.primary,
  },
  modalContent: {},
  modalActions: {
    justifyContent: "space-between",
    padding: "0px 24px 16px 24px",
  },
  buttonItem: {
    display: "block",
    width: "100%",
    textAlign: "right",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "18px",
    color: "#191919",
    fontWeight: theme.typography.fontWeightRegular,
    boxSizing: "border-box",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    "&:focus-visible": {
      outline: "2px solid #191919",
      outlineOffset: "2px",
      borderRadius: "4px",
      textDecoration: "underline",
    },
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

export interface HelpAndResourcesButtonProps {
  yOffset?: number | string; // override Y position for learning wrapper
  guidedTourProps?: Omit<
    GuidedTourTooltipProps,
    "currentStep" | "setCurrentStep" | "parentMenuOpen"
  >;
  currentStep: number;
  setCurrentStep: (newVal: number) => void;
  guidedTourOpen: boolean;
  closeGuidedTour: () => void;
  openGuidedTour: () => void;
  showReminder: boolean;
  sampleProblem?: boolean;
  showModal?: boolean; // determines if the modal for current page shows up
  hideOptions?: HelpAndResourcesOptions[];
  hideButton?: boolean;
  higherEdCourse?: boolean;
}

export const HelpAndResourcesButton = (props: HelpAndResourcesButtonProps) => {
  const classes = useStyles();
  const history = useHistory();
  const { currentStep, setCurrentStep, hideOptions = [], higherEdCourse } = props;

  const userCtx = useContext(UserContext) as UserCtx;

  const universalModalCtx = useContext(UniversalModalContext) as UniversalModalCtx;

  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const currentCourse = courseStructureCtx.courseTreeStructure?.getCurrentCourse();
  const useDigitStudyTool = currentCourse?.metadata.use_digit_study_tool ?? false;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElReminder, setAnchorElReminder] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const reminderOpen = Boolean(anchorElReminder);
  const [tourModalOpen, setTourModalOpen] = useState(false);
  const [showHelpButtonAnimation, setShowHelpButtonAnimation] = useState(false);
  const [seenHelpButton, setSeenHelpButton] = useStorageState({
    storageKey: "lxp-seenHelpButton",
  });

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  useEffect(() => {
    if (seenHelpButton) return;

    // show after 2 seconds
    const showTimer = setTimeout(() => {
      setShowHelpButtonAnimation(true);

      // hide after another 2 seconds
      const hideTimer = setTimeout(() => {
        setShowHelpButtonAnimation(false);
        setSeenHelpButton("true");
      }, 2000);

      // cleanup inner timer
      return () => clearTimeout(hideTimer);
    }, 2000);

    // cleanup outer timer
    return () => clearTimeout(showTimer);
  }, [seenHelpButton, setSeenHelpButton]);

  const handleTourModalClose = () => {
    setTourModalOpen(false);
  };
  const handleTourModalOpen = () => {
    handleClose();
    generateGtmEvent({
      event: "modal",
      action: "open",
      name: "onclick",
      type: "click",
      region: "main content",
      section: "sticky icon",
      text: "guided tour",
    });
    if (props.showModal) {
      setTourModalOpen(true);
    } else {
      handleOpenGuidedTour();
      handleCloseReminder();
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    generateGtmEvent({
      event: "modal",
      action: "open",
      name: "onclick",
      type: "click",
      region: "main content",
      section: "sticky icon",
      text: "help and resources button",
    });
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseReminder = () => {
    setAnchorElReminder(null);
    userCtx.updateDbUser(userCtx.dbUser.id, { dismissed_tour: true });
  };

  useEffect(() => {
    const buttonElement = window.document.querySelector(
      "#help-and-resources"
    ) as HTMLElement;
    const element = window.document.querySelector("#launch-guided-tour") as HTMLElement;

    setAnchorElReminder(element ?? buttonElement);
  }, []);

  const handleOpenGuidedTour = () => {
    props.openGuidedTour();
    setAnchorElReminder(null);
  };

  const handleOverviewClick = () => {
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "modal",
      section: "guided tour",
      text: "digit overview",
    });
    // go to main page
    // initiate guided tour
    history.push({
      pathname: "/learning-overview",
      search: "?guidedtour=" + true,
    });
    handleTourModalClose();
  };
  const handleCurrentClick = () => {
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "modal",
      section: "guided tour",
      text: "current page",
    });
    handleOpenGuidedTour();
    handleTourModalClose();
  };

  const availableMenuItems = [
    {
      label: "Guided tour",
      onClick: handleTourModalOpen,
      component: "button",
      componentType: "button",
      ariaLabel: "View guided tour (opens in dialog box)",
      ariaRole: "menuitem",
      id: "launch-guided-tour",
      itemRef: HelpAndResourcesOptions.GUIDED_TOUR,
    },
    {
      label: "Accessibility",
      onClick: () => {
        handleClose();
        generateGtmEvent({
          event: "modal",
          action: "open",
          name: "onclick",
          type: "click",
          region: "main content",
          section: "sticky icon",
          text: "accessibility",
        });
        universalModalCtx.setUserAccountModalOpen(true);
      },
      component: "button",
      componentType: "button",
      ariaLabel: "View accessibility options (opens in dialog box)",
      ariaRole: "menuitem",
      id: "accessibility",
      itemRef: HelpAndResourcesOptions.ACCESSIBILITY,
    },
    {
      label: "Feedback & Help",
      onClick: () => {
        handleClose();
        generateGtmEvent({
          event: "link",
          action: "click",
          name: "onclick",
          type: "external link",
          region: "main content",
          section: "sticky icon",
          text: "Feedback & help",
        });

        // Open the appropriate link in a new tab
        window.open(getSupportLink(higherEdCourse), "_blank", "noreferrer");
      },
      component: "button",
      componentType: undefined,
      ariaLabel: undefined,
      ariaRole: "link",
      id: "contact-tech-support",
      itemRef: HelpAndResourcesOptions.CONTACT_TECH_SUPPORT,
    },
    {
      label: "Terms of Service",
      onClick: () => {
        handleClose();
        generateGtmEvent({
          event: "modal",
          action: "open",
          name: "onclick",
          type: "click",
          region: "main content",
          section: "sticky icon",
          text: "terms of service",
        });
        universalModalCtx.setTosModalOpen(true);
      },
      component: "button",
      componentType: "button",
      ariaLabel: "View Terms of Service (opens in dialog box)",
      ariaRole: "link",
      id: "terms-of-service",
      itemRef: HelpAndResourcesOptions.TOS,
    },
  ];

  //Do not show guided tour for digit as a study tool
  const filteredMenuItems = useDigitStudyTool
    ? availableMenuItems.filter((item) => item.label !== "Guided tour")
    : availableMenuItems;

  const visibleItems = filteredMenuItems.filter(
    (item) => item.itemRef && !hideOptions.includes(item.itemRef)
  );
  return (
    <>
      {!props.hideButton && (
        <>
          <Box
            className={classes.root}
            sx={{
              ...(props.yOffset
                ? { bottom: props.yOffset, right: "2em" }
                : { bottom: "2em", right: "2em" }),
              ...(props.sampleProblem && { zIndex: 3000 }),
            }}>
            <Tooltip title="Help with using the platform" placement="left" arrow>
              <Box>
                <h4 className={classes.visuallyHidden}>Help with using the platform</h4>
                <IconButtonContained
                  onClick={handleClick}
                  size="large"
                  className={classes.button}
                  aria-label="Help with using the platform"
                  id="help-and-resources"
                  sx={{
                    padding: 0,
                    minWidth: 0,
                    width: showHelpButtonAnimation ? "164px" : "44px",
                    borderRadius: showHelpButtonAnimation ? "100px" : "50%",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}>
                  {showHelpButtonAnimation ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        px: showHelpButtonAnimation ? 2 : 0,
                      }}>
                      <Typography
                        variant="h4"
                        fontSize="18px"
                        fontWeight={700}
                        color="#191919"
                        sx={{
                          mr: "8px",
                          whiteSpace: "nowrap",
                          opacity: showHelpButtonAnimation ? 1 : 0,
                          transition: prefersReducedMotion
                            ? "none"
                            : "opacity 150ms ease-out",
                          transitionDelay: showHelpButtonAnimation
                            ? "100ms" // waits for width animation (100ms)
                            : "0ms",
                          pointerEvents: "none",
                        }}>
                        Need support
                      </Typography>

                      <Typography
                        variant="h4"
                        component="span"
                        sx={{
                          fontSize: "28px",
                          fontWeight: 700,
                          color: "#191919",
                        }}>
                        ?
                      </Typography>
                    </Box>
                  ) : anchorEl ? (
                    <Typography
                      variant="h4"
                      component="span"
                      sx={{ fontSize: "16px", fontWeight: 700 }}>
                      X
                    </Typography>
                  ) : (
                    <Typography
                      variant="h4"
                      component="span"
                      sx={{
                        fontSize: "28px",
                        fontWeight: 700,
                        color: "#191919",
                      }}>
                      ?
                    </Typography>
                  )}
                </IconButtonContained>
              </Box>
            </Tooltip>

            {!open &&
              props.showReminder &&
              !userCtx.dbUser?.dismissed_tour &&
              reminderOpen && (
                <GuidedTourReminder
                  offset={{ left: -218, top: -64 }}
                  closeReminder={handleCloseReminder}
                />
              )}
          </Box>
          <Popover
            id="help-menu-container"
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ horizontal: "left", vertical: "center" }}
            transformOrigin={{ horizontal: "right", vertical: "bottom" }}
            disableScrollLock
            PaperProps={{
              elevation: 0,
              className: classes.paperRoot,
            }}
            sx={props.sampleProblem ? { zIndex: 2000 } : undefined}>
            <Box className={classes.menuRoot}>
              <Box
                sx={{ display: "flex", alignItems: "center", justifyContent: "right" }}>
                <Typography
                  id="help-menu-title"
                  variant="h4"
                  fontSize={"18px"}
                  fontWeight={700}
                  color={"#191919"}
                  sx={{ mr: "8px" }}>
                  Need support
                </Typography>
                <Typography
                  variant="h4"
                  fontSize={"28px"}
                  fontWeight={700}
                  color={"#191919"}>
                  ?
                </Typography>
              </Box>
              <Box role="menu" aria-labelledby="help-menu-title">
                {visibleItems.map((item, index) => {
                  return (
                    <button
                      key={index}
                      onClick={item.onClick}
                      type="button"
                      className={classes.buttonItem}
                      aria-label={item.ariaLabel}
                      role={"menuitem"}
                      id={item.id}>
                      {item.label}
                    </button>
                  );
                })}
                {props.showReminder &&
                  !userCtx.dbUser?.dismissed_tour &&
                  reminderOpen && (
                    <GuidedTourReminder
                      offset={{ left: -218, top: -70 }}
                      closeReminder={handleCloseReminder}
                    />
                  )}
              </Box>
            </Box>
          </Popover>
        </>
      )}
      {/* Show guided tour overlay */}
      {props.guidedTourProps && props.guidedTourOpen && (
        <GuidedTourTooltip
          {...props.guidedTourProps}
          parentMenuOpen={open}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          guidedTourOpen={props.guidedTourOpen}
          closeGuidedTour={props.closeGuidedTour}
          openGuidedTour={props.openGuidedTour}
        />
      )}
      {props.showModal && (
        <Modal
          open={tourModalOpen}
          onClose={handleTourModalClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description">
          <Card className={classes.guidedTourModal}>
            <CardHeader
              sx={{ padding: "16px 24px" }}
              title={
                <Typography variant="h4" className={classes.modalTitle}>
                  Guided Tour
                </Typography>
              }
              action={
                <Tooltip
                  title="Close"
                  placement="bottom"
                  arrow
                  role="tooltip"
                  describeChild>
                  <IconButton
                    onClick={() => {
                      generateGtmEvent({
                        event: "modal",
                        action: "close",
                        name: "onclick",
                        type: "click",
                        region: "main content",
                        section: "guided tour",
                        text: "close cross",
                      });
                      handleTourModalClose();
                    }}
                    aria-label="close guided tour modal"
                    className={classes.modalCloseButton}>
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              }
            />
            <Divider />
            <CardContent sx={{ padding: "24px 32px" }}>
              <Typography variant="h4" className={classes.modalContent}>
                Where would you like to begin from?
              </Typography>
            </CardContent>
            <CardActions className={classes.modalActions}>
              <Button
                variant="contained"
                size="large"
                sx={{ width: "210px", padding: "0px" }}
                onClick={handleOverviewClick}>
                Digit Overview
              </Button>
              <Button
                variant="contained"
                size="large"
                sx={{ width: "210px" }}
                onClick={handleCurrentClick}>
                Current page
              </Button>
            </CardActions>
          </Card>
        </Modal>
      )}
    </>
  );
};
